/**
 * Created by Ella Ma on 4/03/2019.
 * Description:
 * DataImportComponent
 *
 * ------ maintenance history ------
 *  updated by Alan Yang on 5.28 2019
 *  Added SaveScreenDialogOperations.COPY logic.
 */
import { Component, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { DashboardDialogService } from './dashboard-dialog.service';
import { Store, select, createSelector } from '@ngrx/store';
import { AppState } from '../../../redux';
import { filter, take } from 'rxjs/operators';
import * as _ from 'lodash';
import { DualListConfig, DualListEventType } from '../../../widgets/dual-list/dual-list.config';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { DualListItem } from '../../../widgets/dual-list/dual-list.component';
import { SystemUser } from '../../../tamalelibs/models/user.model';
import { ScreenService } from '../../../tamalelibs/services/screen.service';
import { SavedScreen } from '../../../tamalelibs/models/saved-screen.model';
import { ToastService } from '../../../widgets/toast/toast.service';
import { Router } from '@angular/router';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { SaveScreenDialogOperations } from '../../save-screen-dialog/save-screen-dialog.view-model';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { FocusSettingConfig, FocusSettingEventType } from '../focus-setting/focus-setting.model';
import { SystemActionTypes } from '../../../tamalelibs/redux/actions/system.actions';
import { TabItem, TabOptions } from '../../../widgets/tabs/tabs.model';
import { ScreenFocusType } from '../../../models/saved-screen';
import { businessConstants } from '../../../tamalelibs/constants/business.constants';

@Component({
    selector: 'tam-dashboard-dialog',
    templateUrl: './dashboard-dialog.component.html',
    styleUrls: ['./dashboard-dialog.component.scss']
})
export class DashboardDialogComponent implements OnInit, OnDestroy {

    @ViewChild('dualList', { static: false }) dualListInst;
    dialogOpened = false;
    dialogTitle: string;
    tabActive = 0;
    tabsOptions: TabOptions;
    contentForm = new UntypedFormGroup({
        name: new UntypedFormControl(''),
        description: new UntypedFormControl(''),
    });
    get name() {
        return this.contentForm.get('name');
    }
    get description() {
        return this.contentForm.get('description');
    }
    focusSettingConfig: FocusSettingConfig = new FocusSettingConfig();

    dualListConfig: DualListConfig;
    defaultConfig: any = {
        isPublic: true
    };
    currentUser: SystemUser;
    isSaving = false;

    errorMsg: string;
    loading = false;

    private _destroySubscriptions: Subscription[] = [];
    private _opType: string;
    private _oldConfig: any = {
        accessList: [],
        configuration: {
            focus: {
                type: 0,
            },
            widgets: []
        },
        description: null,
        isLandingPage: false,
        isPublic: true,
        name: null,
        type: 'dashboard',
        visible: true
    };
    private _hadModify = false;
    private _accessList: Array<string> = [];

    constructor(
        private _service: DashboardDialogService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
        private _screenService: ScreenService,
        private _toast: ToastService,
        private _router: Router,
        private _alertWindow: AlertWindowService,
    ) { }

    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
        this._initDialogTitle();
        this._initDualListConfig();
        this._initSubscriptions();
        this._initTabs();
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any) {
        if (this.dialogOpened) {
            $event.returnValue = true;
        }
    }

    cancelClick() {
        if (this._isDirty()) {
            const subscription = this._alertWindow.warn('You have unsaved changes',
                ['Are you sure you want to discard all your changes?']).subscribe((result: boolean) => {
                    if (result) {
                        this._onClose();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._onClose();
        }
    }

    onSave() {
        this._doValidation();
        if (this.contentForm.invalid) {
            return;
        }
        if (this._validFocus()) {
            this.errorMsg = 'Please enter information for require field.';
            return;
        }
        if (!this._isDirty()) {
            this._onClose();
            return;
        }
        this.isSaving = true;
        const saveData = this._formatSaveData();
        if (this._opType === SaveScreenDialogOperations.CREATE) {
            this._screenService.create(saveData).pipe(
                take(1)
            ).subscribe(response => this._saveSuccessHandler(response), response => this._savedFailedHandler(response));
        } else if (this._opType === SaveScreenDialogOperations.EDIT) {
            saveData.id = this.defaultConfig.id;
            this._screenService.update(saveData).pipe(
                take(1)
            ).subscribe(response => this._saveSuccessHandler(response), response => this._savedFailedHandler(response));
        } else if (this._opType === SaveScreenDialogOperations.COPY) {
            /** copy dashboard, trigger click save button, do not send api request */
            saveData.configuration.widgets = [];
            this._saveSuccessHandler(saveData);
        }
    }

    private _formatSaveData() {
        const saveData: SavedScreen = {
            type: 'dashboard',
            name: this.name.value,
            description: this.description.value,
            isPublic: this.defaultConfig.isPublic,
            accessList: this.defaultConfig.isPublic ? [] : this._accessList,
            visible: true,
            configuration: {
                focus: {
                    type: this.focusSettingConfig.config.focus.type
                }
            }
        };
        const focus = this.focusSettingConfig.config.focus;
        if (focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
            if (focus.enableFocusEntityType) {
                saveData.configuration.focus.entityTypes = focus.entityTypes || [];
            } else {
                delete saveData.configuration.focus.entityTypes;
            }
            saveData.configuration.focus.enableOneDegree = focus.enableOneDegree;
        } else if (focus.type === ScreenFocusType.SPECIFIC_ENTITY) {
            saveData.configuration.focus.entityId = focus.entityId;
            if (focus.entityRelationshipType) {
                saveData.configuration.focus.relationshipTypes = focus.relationshipTypes || [];
                if (focus.includeSpecificEntityItself) {
                    saveData.configuration.focus.includeSpecificEntityItself = true;
                }
            } else {
                saveData.configuration.focus.relationshipTypes = [];
            }
        } else if (focus.type === ScreenFocusType.CURRENT_USER) {
            if (focus.enableCurrentUserRelationship) {
                saveData.configuration.focus.relationshipTypes = focus.relationshipTypes || [];
            } else {
                saveData.configuration.focus.relationshipTypes = [];
            }
        }
        saveData.isLandingPage = false;
        if (this.defaultConfig.configuration && this.defaultConfig.configuration.widgets) {
            saveData.configuration.widgets = this.defaultConfig.configuration.widgets || [];
        } else {
            saveData.configuration.widgets = [];
        }
        return saveData;
    }

    private _initTabs() {
        const menuItems = [
            new TabItem('Basic'),
            new TabItem('Access List'),
        ];
        this.tabsOptions = new TabOptions(menuItems, 0, false);
        this._destroySubscriptions.push(
            this.tabsOptions.events$.subscribe(event => {
                if (event.type === 'TAB_CHANGED') {
                    this.tabActive = event.payload;
                }
            }),
        );
    }

    private _isDirty(): boolean {
        if (this.loading) {
            return false;
        }
        const newData = this._formatSaveData();
        const oldData = {};
        for (const item in newData) {
            if (newData.hasOwnProperty(item)) {
                oldData[item] = this._oldConfig[item];
            }
        }
        return this.contentForm.dirty || !_.isEqual(oldData, newData) || this._hadModify;
    }

    private _initDialogTitle() {
        this.dialogTitle = 'Add Dashboard';
        if (this._opType === SaveScreenDialogOperations.EDIT || this._opType === SaveScreenDialogOperations.COPY) {
            this.dialogTitle = 'Edit Dashboard Details';
        }
    }

    private _initDashboardData() {
        this.name.setValue(this.defaultConfig.name);
        this.description.setValue(this.defaultConfig.description);
        this.focusSettingConfig.config.focus = this.defaultConfig.configuration.focus;
        if (this.defaultConfig.accessList && this.defaultConfig.accessList.length > 0) {
            this.dualListConfig.dataSource.forEach((data, index) => {
                if (this.defaultConfig.accessList.indexOf(data.data.id) > -1) {
                    data.direction = 'right';
                }
            });
            this._accessList = this.defaultConfig.accessList;
        }
        this._oldConfig = _.cloneDeep(this.defaultConfig);
    }

    private _onChangeAccessList(event) {
        if (event.type === DualListEventType.selectChange) {
            this._accessList = this.dualListInst.getSelectedItem(true);
        }
    }

    private _resetDashboardData() {
        this.tabActive = 0;
        this._hadModify = false;
        this.contentForm.reset();
        this.focusSettingConfig.config.focus.type = ScreenFocusType.ENABLE_FOCUS_CONTROL;
        this.focusSettingConfig.config.focus.entityId = null;
        this.focusSettingConfig.config.focus.entityTypes = [];
        this.focusSettingConfig.config.focus.relationshipTypes = [];
        this.dualListConfig.dataSource.forEach(item => {
            item.direction = 'left';
        });
        this.errorMsg = '';
        this.loading = false;
        this.defaultConfig = {
            isPublic: true
        };
        this._oldConfig = {
            accessList: [],
            configuration: {
                focus: {
                    type: 0,
                },
                widgets: []
            },
            description: null,
            isLandingPage: false,
            isPublic: true,
            name: null,
            type: 'dashboard',
            visible: true
        };
    }

    private _initSubscriptions() {
        this._destroySubscriptions.push(
            this._service._open$.subscribe((config) => {
                if (this.currentUser.credential.publishPubConfiguration) {
                    this._opType = config.opType;
                    this.dialogOpened = true;
                    this.isSaving = false;
                    this.tabActive = 0;
                    this.tabsOptions.selectedIndex = 0;
                    this._initDialogTitle();
                    this._accessList = [this.currentUser.id];
                    this._initDualListData();
                    if ((this._opType === SaveScreenDialogOperations.EDIT || this._opType === SaveScreenDialogOperations.COPY)
                        && config.config) {
                        this.defaultConfig = _.cloneDeep(config.config);
                        this._initDashboardData();
                    }
                }
            })
        );
        this._destroySubscriptions.push(
            this._service._toggleLoading$.subscribe((loading) => {
                if (this.currentUser.credential.publishPubConfiguration) {
                    this.loading = loading;
                    this.isSaving = loading;
                    this.dialogOpened = true;
                    this.isSaving = false;
                    this.tabActive = 0;
                    this.tabsOptions.selectedIndex = 0;
                    this._initDialogTitle();
                }
            })
        );
        this._destroySubscriptions.push(
            this._service._close$.subscribe(() => {
                this._onClose();
            })
        );
        this._destroySubscriptions.push(
            this.focusSettingConfig.events$.pipe(
                filter(item => item.type === FocusSettingEventType.valueChange)
            ).subscribe(() => {
                this.errorMsg = '';
            })
        );
        this._destroySubscriptions.push(
            this.dualListConfig.feedbackSubject$.pipe(
                filter((item) => item.type === DualListEventType.selectChange)
            ).subscribe(() => {
                this._hadModify = true;
            })
        );
    }

    private _initDualListConfig() {
        this.dualListConfig = new DualListConfig();
        this.dualListConfig.titles = [businessConstants.dualList.userNotAllowedAccessTitle, businessConstants.dualList.userAllowedAccessTitle];
        this.dualListConfig.placeholderText = 'Search User or Teams';
        this.dualListConfig.dataTextField = 'name';
        this.dualListConfig.dataValueField = 'id';
        this.dualListConfig.dataSource = [];
        this._destroySubscriptions.push(
            this.dualListConfig.feedbackSubject$.subscribe(data => this._onChangeAccessList(data)),
        );
    }

    private _initDualListData() {
        const accessListSelector = createSelector(
            (state: AppState) => state.users,
            (state: AppState) => state.teams,
            (allUser, allTeam) => {
                if (allUser.length === 0 && allTeam.length === 0) {
                    return [];
                }
                const users = allUser.map(item => {
                    if (item.id === this.currentUser.id && this._opType === SaveScreenDialogOperations.CREATE) {
                        return new DualListItem(item, 'right', false, false);
                    } else {
                        return new DualListItem(item);
                    }
                });
                const teams = allTeam.map(item => {
                    return new DualListItem(Object.assign({ type: 'team' }, item));
                });
                return users.concat(teams);
            }
        );
        this._destroySubscriptions.push(
            this._store.pipe(
                select(accessListSelector),
                filter((list: any) => list.length > 0),
            ).subscribe(list => {
                this.dualListConfig.dataSource = list;
            })
        );
    }

    private _validFocus(): boolean {
        let inValid;
        const focus = this.focusSettingConfig.config.focus;
        if (focus.type === ScreenFocusType.ENABLE_FOCUS_CONTROL) {
            inValid = focus.enableFocusEntityType && (!focus.entityTypes || focus.entityTypes.length === 0);
        } else if (focus.type === ScreenFocusType.SPECIFIC_ENTITY) {
            inValid = !focus.entityId || (focus.entityRelationshipType
                && (!focus.relationshipTypes || focus.relationshipTypes.length === 0));
        } else if (focus.type === ScreenFocusType.CURRENT_USER) {
            inValid = focus.enableCurrentUserRelationship && (!focus.relationshipTypes || focus.relationshipTypes.length === 0);
        } else {
            inValid = false;
        }
        return inValid;
    }

    private _onClose(data?) {
        this.dialogOpened = false;
        this._resetDashboardData();
        this._service._result$.next({
            type: 'CLOSE',
            payload: data
        });
    }

    private _doValidation() {
        this.name.setErrors(null);
        this.description.setErrors(null);

        if (!this.name.value) {
            this.name.setErrors({
                required: true
            });
        } else if (this.name.value.length > 128) {
            this.name.setErrors({
                maxlength: true
            });
        } else if (this.name.value.includes('/') || this.name.value.includes('\'')) {
            this.name.setErrors({
                specialCharacter: true
            });
        }

        if (this.description.value && this.description.value.length > 256) {
            this.description.setErrors({
                maxlength: true
            });
        }
    }

    private _saveSuccessHandler(response) {
        this.isSaving = false;
        this._onClose(response);
        let message = '';
        if (this._opType === SaveScreenDialogOperations.CREATE) {
            message = `${response.name} successfully saved.`;
        } else if (this._opType === SaveScreenDialogOperations.EDIT) {
            message = `${response.name} successfully updated.`;
        }
        if (this._opType !== SaveScreenDialogOperations.COPY) {
            this._toast.notify({
                message: message,
                style: NotificationStyles.Success
            });
        }
        if (this._opType === SaveScreenDialogOperations.CREATE) {
            this._router.navigate(['dashboard/create', response.id]);
        }
        // update left dashboard menu
        if (JSON.stringify(response) === '{}') {
            return;
        }
        if (this._opType === SaveScreenDialogOperations.CREATE) {
            this._store.dispatch({
                type: SystemActionTypes.ADD_SCREEN,
                payload: {
                    type: 'dashboard',
                    ...response
                },
            });
        } else if (this._opType === SaveScreenDialogOperations.EDIT) {
            this._store.dispatch({
                type: SystemActionTypes.EDIT_SCREEN,
                payload: {
                    type: 'dashboard',
                    ...response
                },
            });
        }
    }

    private _savedFailedHandler(error) {
        this.isSaving = false;
        if (error.message.includes('ERR_NAME_EXISTED')) {
            this.name.setErrors({
                duplicated: true
            });
        }
    }
}
